import { useMemo } from "react";

import { curveBasis } from "@visx/curve";
import { LinearGradient } from "@visx/gradient";
import { ParentSize, ScaleSVG } from "@visx/responsive";
import { scaleLinear, scaleTime } from "@visx/scale";
import { AreaClosed, LinePath } from "@visx/shape";

import { TimeSeriesData } from "@app/common/types";

export interface TimeSeriesChartParentProps {
  average: number | null;
  data: TimeSeriesData[];
  /** [min, max] of possible x-axis values */
  domain: [number, number];
  height?: number;
  /** [min, max] of possible y-axis values */
  range: [number, number];
}

interface TimeSeriesChartProps extends TimeSeriesChartParentProps {
  height: number;
  width: number;
}

const TimeSeriesChart = ({
  average,
  data,
  domain,
  height,
  range,
  width,
}: TimeSeriesChartProps) => {
  const xScale = useMemo(
    () =>
      scaleTime<number>({
        domain,
        range: [0, width],
      }),
    [width, domain]
  );

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: range,
        range: [height, 0],
      }),
    [height, range]
  );

  return (
    <ScaleSVG height={height} width={width} preserveAspectRatio="none">
      <LinearGradient
        id="time-series-area-gradient"
        from="#4C47CC"
        to="#4C47CC"
        fromOpacity="20%"
        toOpacity="0%"
      />
      {/* Chart area */}
      <AreaClosed<TimeSeriesData>
        data={data}
        yScale={yScale}
        x={(d) => xScale(d.x)}
        y={(d) => yScale(d.y)}
        fill="url(#time-series-area-gradient)"
        curve={curveBasis}
      />
      <LinePath<TimeSeriesData>
        data={data}
        curve={curveBasis}
        x={(d) => xScale(d.x)}
        y={(d) => yScale(d.y)}
        strokeWidth={2}
        stroke="#3D39A3"
      />
      {/* Average line */}
      {average != null && (
        <LinePath<TimeSeriesData>
          data={data}
          x={(d) => xScale(d.x)}
          y={() => yScale(average)}
          strokeWidth={2}
          stroke="#C1C1C1"
          strokeDasharray="2 4"
        />
      )}
    </ScaleSVG>
  );
};

const TimeSeriesChartParent = (props: TimeSeriesChartParentProps) => (
  <ParentSize>
    {({ width, height }) => (
      <TimeSeriesChart
        width={Math.max(0, width)}
        height={Math.max(0, height)}
        {...props}
      />
    )}
  </ParentSize>
);

export default TimeSeriesChartParent;
